import React, { useRef, useEffect, useCallback } from 'react';
import { makeStyles, Theme, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { NumericMetricInfo } from '@moderepo/mode-apis';
import { ModeTheme } from '@moderepo/mode-app-components';


const useStyle = makeStyles((theme: Theme) => {
    return {

        root: {
            width    : '100%',
            minHeight: 200,
            position : 'relative',
        },

        placeHolderImage: {
            width   : '100%',
            position: 'relative',
            display : 'inline-block',
        },

        chart: {
            position: 'absolute',
            top     : 0,
            left    : 0,
            width   : '100%',
            height  : '100%',
        },

        metricValueName: {
            color                       : theme.palette.text.secondary,
            fontWeight                  : 500,
            fontSize                    : 16,
            textAlign                   : 'center',
            [theme.breakpoints.up('md')]: {
                fontSize: 18,
            },
        },

        metricValueDate: {
            color                       : theme.palette.text.secondary,
            fontSize                    : 14,
            textAlign                   : 'center',
            [theme.breakpoints.up('md')]: {
                fontSize: 16,
            },
        },

        metricValueUnit: {
            paddingLeft: 5,
        },
    };
});


// This is a dataURI of a 3x2 blank image used for keeping the chart scale proportionally
// eslint-disable-next-line max-len
const placeHolderImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADwAAAAoCAYAAACiu5n/AAAAAXNSR0IArs4c6QAAAGBJREFUaEPt1AERAEAIhEDtX/oDfANcGwjM7Ry7PfbveLhunGGGYwQkHRP6vcMwwzECko4JNVqSlnSMgKRjQq20pCUdIyDpmFArLWlJxwhIOibUSkta0jECko4JtdLnkn4pyQAp/G0vFAAAAABJRU5ErkJggg==';


export enum GaugeColorType {
    // Color the gauge in GRADIENT color from positive color to negative color.
    POSITIVE_TO_NEGATIVE = 'positive_to_negative',
    // Color the gauge in GRADIENT color from negative color to positive color.
    NEGATIVE_TO_POSITIVE = 'negative_to_positive',
    // Color the gauge in GRADIENT color from negative color to positive color to negative color.
    NEGATIVE_TO_POSITIVE_TO_NEGATIVE = 'negative_to_positive_to_negative',
    // Color the gauge in GRADIENT color from positive color to negative color to positive
    POSITIVE_TO_NEGATIVE_TO_POSITIVE = 'positive_to_negative_to_positive',
    // Color the gauge based on some thresholds. The threshold will be configured with the GaugeChart 'thresholds' option.
    THRESHOLDS = 'threshold',
    // Color the gauge with 1 solid color. The color can be configured with the GaugeChart 'style' option.
    SOLID = 'solid',
}


export interface GaugeThreshold {
    readonly criticallyLow?: number | undefined;
    readonly low?: number | undefined;
    readonly high?: number | undefined;
    readonly criticallyHigh?: number | undefined;
}



export interface GaugeChartStyleOptions {
    readonly gaugeColor?: string;
    readonly handColor?: string;
    readonly valueColor?: string;
}


const createThresholdRanges = (thresholds: GaugeThreshold, minValue: number, maxValue: number, theme: ModeTheme) => {
    const result: {
        start: number;
        end: number;
        color: string;
    }[] = [{
        start: minValue,
        end  : thresholds.criticallyLow ?? minValue,
        color: theme.palette.error.main,
    }, {
        start: thresholds.criticallyLow ?? minValue,
        end  : thresholds.low ?? thresholds.criticallyLow ?? minValue,
        color: theme.palette.warning.main,
    }, {
        start: thresholds.low ?? thresholds.criticallyLow ?? minValue,
        end  : thresholds.high ?? thresholds.criticallyHigh ?? maxValue,
        color: theme.palette.success.main,
    }, {
        start: thresholds.high ?? thresholds.criticallyHigh ?? maxValue,
        end  : thresholds.criticallyHigh ?? maxValue,
        color: theme.palette.warning.main,
    }, {
        start: thresholds.criticallyHigh ?? maxValue,
        end  : maxValue,
        color: theme.palette.error.main,
    }];

    return result;
};


/**
 * Create a generic Axis which can be used to display values that are just value, no special meaning. These are the kind of values that doesn't
 * have special meaning e.g. low value means bad and high values mean good.
 */
const createNeutralAxis = (
    theme: ModeTheme,
    chart: am4charts.GaugeChart,
    value: number | undefined,
    valueRange: {readonly min?: number | undefined, readonly max?: number | undefined} | undefined,
    style?: GaugeChartStyleOptions,
): any => {

    // Create the axis
    const axis = chart.xAxes.push(new am4charts.ValueAxis() as any);
    axis.strictMinMax = true;
    axis.renderer.inside = true;
    axis.renderer.minGridDistance = 100;
    axis.renderer.line.strokeOpacity = 1;
    axis.renderer.line.strokeWidth = 5;
    axis.renderer.line.stroke = am4core.color(style?.gaugeColor || theme?.palette.text.secondary);
    if (valueRange) {
        axis.min = valueRange.min;
        axis.max = valueRange.max;
    } else if (value !== undefined) {
        // If range is not set, automatically set the min/max so that the value is in the middle.
        // We will make min = 0 and max = 2 * value. This mean if value is 20, min = 0 and max = 40.
        axis.min = 0;
        axis.max = value * 2;
    } else {
        // If range is not set and we don't have a value either, use 0 to 100 as range
        axis.min = 0;
        axis.max = 100;
    }


    // Customize the axis
    axis.renderer.ticks.template.disabled = false;
    axis.renderer.ticks.template.stroke = am4core.color(theme.palette.text.secondary);
    axis.renderer.ticks.template.strokeOpacity = 0.5;
    axis.renderer.ticks.template.length = 7;
    axis.renderer.grid.template.disabled = true;
    axis.renderer.labels.template.radius = 30;
    axis.renderer.labels.template.fontSize = '0.9em';
    axis.renderer.labels.template.stroke = am4core.color(theme.palette.text.secondary);

    return axis;
};



/**
 * Create Gauge axis for a gauge that show Good/Bad values. This kind of gauge will have a green -> red arc which can be used for value which has
 * value that goes from good to bad e.g. 0 = good and 100 = bad. This gauge will have red -> green arc for the kind of value that has the opposite
 * meaning.
 *
 * @param chart
 * @param isReverse
 */
const createPositiveNegativeAxis = (
    theme: ModeTheme,
    chart: am4charts.GaugeChart,
    value: number | undefined,
    valueColorType: GaugeColorType,
    valueRange: {readonly min?: number | undefined, readonly max?: number | undefined} | undefined,
    thresholds?: GaugeThreshold | undefined,
): any => {

    // Create a gradient color
    const gradient = new am4core.LinearGradient();

    // Create the axis
    const axis = chart.xAxes.push(new am4charts.ValueAxis() as any);
    axis.strictMinMax = true;
    axis.renderer.inside = true;
    axis.renderer.minGridDistance = 100;
    axis.renderer.line.strokeOpacity = 1;
    axis.renderer.line.strokeWidth = 15;
    axis.renderer.line.stroke = gradient;
    if (valueRange) {
        axis.min = valueRange.min;
        axis.max = valueRange.max;
    } else if (value !== undefined) {
        // If range is not set, automatically set the min/max so that the value is in the middle.
        // We will make min = 0 and max = 2 * value. This mean if value is 20, min = 0 and max = 40.
        axis.min = 0;
        axis.max = value * 2;
    } else {
        // If range is not set and we don't have a value either, use 0 to 100 as range
        axis.min = 0;
        axis.max = 100;
    }


    // Customize the axis
    axis.renderer.ticks.template.disabled = false;
    axis.renderer.ticks.template.stroke = am4core.color(theme.palette.text.secondary);
    axis.renderer.ticks.template.strokeOpacity = 0.5;
    axis.renderer.ticks.template.length = 15;
    axis.renderer.grid.template.disabled = true;
    axis.renderer.labels.template.radius = 40;
    axis.renderer.labels.template.fontSize = '0.9em';
    axis.renderer.labels.template.stroke = am4core.color(theme.palette.text.secondary);


    // Draw the gauge
    if (valueColorType === GaugeColorType.THRESHOLDS && thresholds) {
        // If gauge color type is THRESHOLDS and we have threshold settings, color the gauge based on the threshold values
        const ranges = createThresholdRanges(thresholds, axis.min, axis.max, theme);
        ranges.forEach((range) => {
            const axisRange = axis.axisRanges.create();
            axisRange.axisFill.fill = am4core.color(range.color);
            axisRange.axisFill.fillOpacity = 1;
            axisRange.axisFill.zIndex = -1;
            axisRange.value = range.start;
            axisRange.endValue = range.end;
            axisRange.grid.strokeOpacity = 0;
        });
    } else if (valueColorType === GaugeColorType.POSITIVE_TO_NEGATIVE) {
        // Fill the gauge with gradient color from good to bad
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.warning.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.error.main) });
    } else if (valueColorType === GaugeColorType.NEGATIVE_TO_POSITIVE) {
        // Fill the gauge with gradient color from bad to good
        gradient.stops.push({ color: am4core.color(theme.palette.error.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.warning.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
    } else if (valueColorType === GaugeColorType.POSITIVE_TO_NEGATIVE_TO_POSITIVE) {
        // Fill the gauge with gradient color from good to bad to good
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.warning.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.error.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.warning.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
    } else if (valueColorType === GaugeColorType.NEGATIVE_TO_POSITIVE_TO_NEGATIVE) {
        // Fill the gauge with gradient color from bad to good to bad
        gradient.stops.push({ color: am4core.color(theme.palette.error.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.warning.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.warning.main) });
        gradient.stops.push({ color: am4core.color(theme.palette.error.main) });
    } else {
        gradient.stops.push({ color: am4core.color(theme.palette.success.main) });
    }

    return axis;
};


interface GaugeChartProps {
    readonly chartId: string;
    readonly className?: string;
    readonly metric: NumericMetricInfo;
    readonly value?: number | undefined;
    readonly displayValue?: string | undefined;
    // Custom value range to use if we don't want to use the numericMetric's range
    readonly range?: {
        readonly min?: number | undefined;
        readonly max?: number | undefined;
    } | undefined;
    readonly colorType: GaugeColorType;
    readonly thresholds?: GaugeThreshold | undefined;
    readonly style?: GaugeChartStyleOptions;
    readonly showMetricName?: 'top' | 'bottom' | undefined;
}


/**
 * This is a Component that use a Gauge to display metric value. There are a few different UI for a Gauge and the user can customize it by
 * specifying the ChartValueType or props.style options.
 *
 * @param props
 */
export const GaugeChart = (props: GaugeChartProps) => {
    const chartRef = useRef<am4charts.GaugeChart>();

    // Refs to keep track of which prop changes so that we don't re-create new chart every time
    const chartIdRef = useRef<string>();
    const metricRef = useRef<NumericMetricInfo>();
    const valueRef = useRef<number>();
    const rangeRef = useRef<GaugeChartProps['range']>();
    const colorTypeRef = useRef<GaugeColorType>();
    const thresholdsRef = useRef<GaugeThreshold>();
    const styleRef = useRef<GaugeChartStyleOptions>();
    const showMetricNameRef = useRef<GaugeChartProps['showMetricName']>();

    // Refs to chart labels
    const valueLabelRef = useRef<am4core.Label>();
    const unitLabelRef = useRef<am4core.Label>();
    const metricNameLabelRef = useRef<am4core.Label>();

    const classes = useStyle();
    const theme = useTheme() as ModeTheme;


    /**
     * Update gauge's handle and value label based on the current value
     */
    const updateGaugeValue = useCallback(() => {

        const chart = chartRef.current as am4charts.GaugeChart;
        if (chart && chart.hands.length > 0) {
            const hand: am4charts.ClockHand = chart.hands.getIndex(0) as am4charts.ClockHand;
            const axis = chart.xAxes.getIndex(0) as any;

            // Update hand but make sure the hand doesn't go below MIN value or over MAX value.
            if (props.value !== undefined) {
                const value = Math.max(axis.min as number, Math.min(axis.max as number, props.value));
                hand.showValue(value);
                hand.show();
            } else {
                hand.hide();
            }

            // Update label to show the value. For label, we can show the actual value, doesn't matter if it is over or under min/max
            if (valueLabelRef.current) {
                valueLabelRef.current.text = props.displayValue ?? props.value?.toString() ?? '';
            }

            if (metricNameLabelRef.current) {
                metricNameLabelRef.current.text = props.metric.evaluator?.displayName
                                                ?? props.metric.displayName
                                                ?? props.metric.name;
            }
        }
    }, [props.displayValue, props.metric.displayName, props.metric.evaluator?.displayName, props.metric.name, props.value]);


    // This useEffect is used for cleaning up Chart when component unmounted
    useEffect(() => {
        // Return cleanup function
        return () => {
            const chart = chartRef.current as am4charts.GaugeChart;
            if (chart && !chart.isDisposed()) {
                // console.log('dispose chart');

                // dispose chart
                chart.dispose();
                chartRef.current = undefined;
            }
        };
    }, []);



    /**
     * This useEffect is used for creating/updating the chart. This useEffect depends on a lot of props and when each props change, this useEffect
     * will be called. So, it will be very slow if we were to re-create the chart every time this useEffect is called. We can't remove any of the
     * dependencies because the chart needs all the dependencies. So to solve this problem, we will keep a reference of the PREVIOUS prop values.
     * Each time this useEffect is called, we will compare the new prop values with the previous to determine which props changed. Some props
     * will always require a new chart to be created e.g. displayFields when they are changed. Some props does not which mean we can
     * just update the chart's properties when component props changed.
     */
    useEffect(() => {

        let chart = chartRef.current as am4charts.GaugeChart;

        // We will need to re-create chart if some of these props changed
        const createNewChart: boolean = props.chartId !== chartIdRef.current
            || (props.metric !== metricRef.current && JSON.stringify(props.metric) !== JSON.stringify(metricRef.current))
            || props.value !== valueRef.current
            || (props.range !== rangeRef.current && JSON.stringify(props.range) !== JSON.stringify(rangeRef.current))
            || props.colorType !== colorTypeRef.current
            || (props.thresholds !== thresholdsRef.current && JSON.stringify(props.thresholds) !== JSON.stringify(thresholdsRef.current))
            || (props.style !== styleRef.current && JSON.stringify(props.style) !== JSON.stringify(styleRef.current))
            || props.showMetricName !== showMetricNameRef.current;


        if (!chart || createNewChart) {

            if (chart && !chart.isDisposed()) {
                // Dispose old chart first
                // console.log('dispose chart');

                // dispose chart
                chart.dispose();
                chartRef.current = undefined;
            }

            // create new Chart
            chart = am4core.create(props.chartId, am4charts.GaugeChart);
            chart.hiddenState.properties.opacity = 0; // this makes initial fade in effect
            chart.innerRadius = -15;
            chart.startAngle = 160;
            chart.endAngle = 380;
            chart.resizable = true;

            // Create a label for displaying the unit
            if (unitLabelRef.current && !unitLabelRef.current.isDisposed()) {
                unitLabelRef.current.dispose();
            }
            unitLabelRef.current = chart.radarContainer.createChild(am4core.Label);
            unitLabelRef.current.parent = chart.chartContainer;
            unitLabelRef.current.isMeasured = false;
            unitLabelRef.current.fill = am4core.color(theme.palette.text.secondary);
            unitLabelRef.current.fontSize = 20;
            unitLabelRef.current.horizontalCenter = 'middle';
            unitLabelRef.current.verticalCenter = 'middle';
            unitLabelRef.current.x = am4core.percent(50);
            unitLabelRef.current.y = am4core.percent(55);

            // Create a label for displaying the value
            if (valueLabelRef.current && !valueLabelRef.current.isDisposed()) {
                valueLabelRef.current.dispose();
            }
            valueLabelRef.current = chart.radarContainer.createChild(am4core.Label);
            valueLabelRef.current.parent = chart.chartContainer;
            valueLabelRef.current.isMeasured = false;
            valueLabelRef.current.fill = am4core.color(props.style?.valueColor ?? theme.palette.text.primary);
            valueLabelRef.current.fontSize = 30;
            valueLabelRef.current.fontWeight = 'bold';
            valueLabelRef.current.horizontalCenter = 'middle';
            valueLabelRef.current.verticalCenter = 'middle';
            valueLabelRef.current.x = am4core.percent(50);
            valueLabelRef.current.y = am4core.percent(85);
            valueLabelRef.current.text = '0';

            // Create a label for displaying the metric name
            if (props.showMetricName) {
                if (metricNameLabelRef.current && !metricNameLabelRef.current.isDisposed()) {
                    metricNameLabelRef.current.dispose();
                }
                metricNameLabelRef.current = chart.radarContainer.createChild(am4core.Label);
                metricNameLabelRef.current.parent = chart.chartContainer;
                metricNameLabelRef.current.fill = am4core.color(theme.palette.text.primary);
                metricNameLabelRef.current.isMeasured = false;
                metricNameLabelRef.current.fontSize = 20;
                metricNameLabelRef.current.horizontalCenter = 'middle';
                metricNameLabelRef.current.verticalCenter = 'middle';
                metricNameLabelRef.current.x = am4core.percent(50);
                if (props.showMetricName === 'top') {
                    metricNameLabelRef.current.y = am4core.percent(0);
                } else {
                    metricNameLabelRef.current.y = am4core.percent(100);
                }
                metricNameLabelRef.current.text = '';
            }


            // Draw axis/gauge
            while (chart.xAxes.length > 0) {
                chart.xAxes.removeIndex(0).dispose();
            }

            if (unitLabelRef.current && props.metric && props.metric.unit) {
                unitLabelRef.current.text = props.metric.unit;
            }

            // Create Axis, the Gauge UI
            switch (props.colorType) {
                case GaugeColorType.SOLID:
                    createNeutralAxis(theme, chart, props.value, props.range ?? props.metric.range, props.style);
                    break;
                default:
                    createPositiveNegativeAxis(
                        theme, chart, props.value, props.colorType, props.range ?? props.metric.range, props.thresholds,
                    );
                    break;
            }


            // IMPORTANT NOTE: need to create hand AFTER axis
            while (chart.hands.length > 0) {
                chart.hands.removeIndex(0).dispose();
            }

            const hand = chart.hands.push(new am4charts.ClockHand());
            hand.startWidth = 5;
            hand.endWidth = 0;
            hand.radius = am4core.percent(90);
            hand.fill = am4core.color(props.style?.handColor || theme.palette.text.primary);
            hand.stroke = am4core.color(props.style?.handColor || theme.palette.text.primary);
            hand.showValue(0);
        }

        // Update the refs so that we can compare them again later
        chartIdRef.current = props.chartId;
        metricRef.current = props.metric;
        valueRef.current = props.value;
        rangeRef.current = props.range;
        colorTypeRef.current = props.colorType;
        thresholdsRef.current = props.thresholds;
        styleRef.current = props.style;
        showMetricNameRef.current = props.showMetricName;
        chartRef.current = chart;

        if (createNewChart) {
            updateGaugeValue();
        }

    }, [props.chartId, props.metric, props.colorType, props.style, theme.palette.text.secondary, theme.palette.text.primary, theme,
        props.showMetricName, props.value, props.range, props.thresholds, updateGaugeValue]);


    /**
     * This useEffect is for monitoring the props.data value change to update the chart hand and value display.
     */
    useEffect(() => {
        updateGaugeValue();
    }, [updateGaugeValue]);



    return (
        <div className={clsx(classes.root, props.className)}>
            <div className={classes.chart} id={props.chartId} />
            {/* We need this 3x2 placeholder image to keep the gauge chart scale properly */}
            <img className={classes.placeHolderImage} src={placeHolderImage} alt="placeHolderImage" />
        </div>
    );
};
